import * as THREE from "three";
import vertSrc from "./shaders/page.vert.glsl?raw";
import fragSrc from "./shaders/page.frag.glsl?raw";

export interface PageOptions {
  width: number;
  height: number;
  segments?: number;
  frontTexture?: THREE.Texture | null;
  backTexture?: THREE.Texture | null;
}

/**
 * A single sheet of paper in the book.
 *
 * Local frame:
 *   - Spine is at x = 0 (mesh's local origin sits on the spine, page extends along +x).
 *   - The mesh is built so vertices span x: [0..width], y: [-height/2..+height/2].
 *   - Flipping is achieved entirely in the vertex shader via uFlip (in radians).
 *
 * Flip semantics:
 *   - uFlip = 0     -> page lies flat to the right (showing front).
 *   - uFlip = -PI   -> page lies flat to the left (showing back / next spread).
 *   - We animate uFlip from 0 to -PI for a forward flip, and back to 0 for a reverse flip.
 */
export class Page {
  readonly mesh: THREE.Mesh;
  readonly material: THREE.ShaderMaterial;
  readonly width: number;
  readonly height: number;

  constructor(opts: PageOptions) {
    const { width, height, segments = 40 } = opts;
    this.width = width;
    this.height = height;

    // Plane geometry: by default it's centered at origin spanning [-w/2..w/2].
    // We translate so x: [0..width], leaving spine at x=0.
    const geom = new THREE.PlaneGeometry(width, height, segments, 4);
    geom.translate(width / 2, 0, 0);

    const blank = makeBlankTexture();

    this.material = new THREE.ShaderMaterial({
      vertexShader: vertSrc,
      fragmentShader: fragSrc,
      side: THREE.DoubleSide,
      transparent: false,
      uniforms: {
        uFlip: { value: 0 },
        uWidth: { value: width },
        uHeight: { value: height },
        uCurl: { value: 1.0 },
        uFrontMap: { value: opts.frontTexture ?? blank },
        uBackMap: { value: opts.backTexture ?? blank },
        uHasFront: { value: opts.frontTexture ? 1 : 0 },
        uHasBack: { value: opts.backTexture ? 1 : 0 },
        uLightDir: { value: new THREE.Vector3(0.4, 0.8, 0.6).normalize() },
        uTint: { value: new THREE.Color(1, 1, 1) },
      },
    });

    this.mesh = new THREE.Mesh(geom, this.material);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
  }

  get flip(): number {
    return this.material.uniforms.uFlip.value as number;
  }
  set flip(v: number) {
    this.material.uniforms.uFlip.value = v;
  }

  setFrontTexture(tex: THREE.Texture | null) {
    const blank = makeBlankTexture();
    this.material.uniforms.uFrontMap.value = tex ?? blank;
    this.material.uniforms.uHasFront.value = tex ? 1 : 0;
  }

  setBackTexture(tex: THREE.Texture | null) {
    const blank = makeBlankTexture();
    this.material.uniforms.uBackMap.value = tex ?? blank;
    this.material.uniforms.uHasBack.value = tex ? 1 : 0;
  }

  dispose() {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}

let _blank: THREE.Texture | null = null;
function makeBlankTexture(): THREE.Texture {
  if (_blank) return _blank;
  const c = document.createElement("canvas");
  c.width = c.height = 2;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#f5ecdf";
  ctx.fillRect(0, 0, 2, 2);
  _blank = new THREE.CanvasTexture(c);
  _blank.colorSpace = THREE.SRGBColorSpace;
  return _blank;
}
